import { useState } from "react";
import { ArrowUpRight, Check, Cloud, Lock, Sparkles, Star } from "lucide-react";

// Digtek-styled repackaging of Aximo's 3 tiers on a light bg.
const tiers = [
    {
        id: "starter",
        name: "Starter",
        eyebrow: "For founders validating a channel",
        monthly: "$2,199",
        annual: "$1,979",
        foundingRate: "Founding rate — locked for 12 months",
        blurb: "One anchor channel, weekly cadence, dashboards that prove which content earns your buyer.",
        features: [
            "Weekly content on 1 anchor channel",
            "1 social platform, 3-day turnaround",
            "Basic SEO + caption optimization",
            "1 language, monthly reporting",
            "Email support",
        ],
        cta: "Get started",
        highlighted: false,
    },
    {
        id: "studio",
        name: "Studio",
        eyebrow: "For brands compounding to authority",
        monthly: "$3,499",
        annual: "$3,149",
        blurb: "Multi-channel content engine, dedicated team, weekly creative-director calls. The plan most brands scale on.",
        features: [
            "Weekly cadence across 3 social channels",
            "Weekly calls with creative director",
            "Premium real-human dubbing",
            "Dedicated account manager + editors",
            "1 language incl. — +$499/mo per regional language",
            "Bi-weekly performance reviews",
        ],
        cta: "Most popular",
        highlighted: true,
    },
    {
        id: "sprint",
        name: "Sprint",
        eyebrow: "For launches that need to own the room",
        monthly: "$5,499",
        annual: "$4,949",
        blurb: "Cinematic-grade content, dedicated creative director, weekly strategy calls. Reserved for 5 brands per quarter.",
        features: [
            "Full coverage across 3 channels",
            "Cinematic edits + motion graphics",
            "Weekly calls with creative director",
            "1 language incl. — +$499/mo per regional language",
            "Exclusive industry-expert interviews",
            "24h white-glove SLA",
        ],
        cta: "Apply for Sprint",
        highlighted: false,
        trustSignal: "By application · founder-led onboarding",
    },
];

export default function DigtekPricing() {
    const [billing, setBilling] = useState("monthly");

    return (
        <section
            id="pricing"
            data-testid="digtek-pricing"
            className="relative py-24 lg:py-32 bg-white overflow-hidden"
        >
            <div className="digtek-blob purple w-[380px] h-[380px] -left-24 top-24 opacity-40" />
            <div className="digtek-blob lime w-[340px] h-[340px] -right-24 bottom-24 opacity-40" />

            <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
                <div className="text-center mb-12">
                    <div className="digtek-eyebrow mb-4 justify-center">
                        <span>Our pricing plans</span>
                    </div>
                    <h2
                        data-testid="pricing-heading"
                        className="font-display font-bold text-[40px] sm:text-[52px] lg:text-[64px] leading-[1.02] text-digtek-header max-w-[820px] mx-auto"
                    >
                        Simple pricing.
                        <br />
                        <em className="not-italic text-digtek-purple">
                            No 60-page proposals.
                        </em>
                    </h2>
                    <p className="mt-6 text-digtek-text text-lg max-w-[620px] mx-auto">
                        Three plans, transparent monthly billing, no lock-ins.
                        Pick the shape that matches your ambition — we'll help on
                        a 30-minute call.
                    </p>
                </div>

                {/* Billing toggle */}
                <div className="flex items-center justify-center gap-4 mb-12">
                    <div
                        className="inline-flex items-center bg-digtek-bg rounded-full p-1.5"
                        data-testid="pricing-billing-toggle"
                    >
                        {["monthly", "annual"].map((b) => (
                            <button
                                key={b}
                                onClick={() => setBilling(b)}
                                data-testid={`pricing-billing-${b}`}
                                className={`px-6 h-11 rounded-full text-sm font-bold uppercase tracking-wider transition-all ${
                                    billing === b
                                        ? "bg-digtek-header text-digtek-lime"
                                        : "text-digtek-header/60 hover:text-digtek-header"
                                }`}
                            >
                                {b}
                            </button>
                        ))}
                    </div>
                    <span className="text-sm font-bold text-digtek-purple bg-digtek-lime px-4 py-1.5 rounded-full">
                        Save 10%
                    </span>
                </div>

                {/* Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {tiers.map((t) => {
                        const price = billing === "annual" ? t.annual : t.monthly;
                        return (
                            <div
                                key={t.id}
                                data-testid={`pricing-card-${t.id}`}
                                className={`relative rounded-3xl p-8 lg:p-10 transition-all hover:-translate-y-2 ${
                                    t.highlighted
                                        ? "bg-digtek-header text-white shadow-[0_40px_80px_-20px_rgba(23,1,44,.35)]"
                                        : "bg-white border border-digtek-border shadow-[0_4px_25px_rgba(23,1,44,.05)]"
                                }`}
                            >
                                {t.highlighted && (
                                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-digtek-lime text-digtek-header text-[11px] uppercase tracking-wider font-bold px-4 py-1.5 rounded-full inline-flex items-center gap-1.5">
                                        <Star className="w-3 h-3 fill-current" />
                                        Most popular
                                    </span>
                                )}

                                <div className="flex items-center justify-between mb-4">
                                    <p
                                        className={`text-[13px] uppercase tracking-wider font-bold ${
                                            t.highlighted ? "text-digtek-lime" : "text-digtek-purple"
                                        }`}
                                    >
                                        {t.name}
                                    </p>
                                    <span
                                        className={`w-11 h-11 rounded-full flex items-center justify-center ${
                                            t.highlighted
                                                ? "bg-digtek-lime text-digtek-header"
                                                : "bg-digtek-bg text-digtek-purple"
                                        }`}
                                    >
                                        <Cloud className="w-5 h-5" />
                                    </span>
                                </div>

                                <p
                                    className={`text-sm mb-4 ${
                                        t.highlighted ? "text-white/70" : "text-digtek-text"
                                    }`}
                                >
                                    {t.eyebrow}
                                </p>

                                <div className="flex items-end gap-2 mb-3">
                                    <span
                                        className={`font-display font-bold text-5xl lg:text-6xl leading-none ${
                                            t.highlighted ? "text-white" : "text-digtek-header"
                                        }`}
                                    >
                                        {price}
                                    </span>
                                    <span
                                        className={`text-sm mb-2 font-medium ${
                                            t.highlighted ? "text-white/60" : "text-digtek-text"
                                        }`}
                                    >
                                        / month
                                    </span>
                                </div>

                                {t.foundingRate && (
                                    <div
                                        data-testid={`pricing-founding-${t.id}`}
                                        className={`inline-flex items-center gap-1.5 mb-5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.14em] font-bold ${
                                            t.highlighted
                                                ? "bg-digtek-lime/15 text-digtek-lime border border-digtek-lime/30"
                                                : "bg-digtek-lime/20 text-digtek-header border border-digtek-purple/20"
                                        }`}
                                    >
                                        <Sparkles className="w-3 h-3" />
                                        {t.foundingRate}
                                    </div>
                                )}

                                <p
                                    className={`text-[15px] leading-relaxed mb-6 ${
                                        t.highlighted ? "text-white/80" : "text-digtek-text"
                                    }`}
                                >
                                    {t.blurb}
                                </p>

                                {t.trustSignal && (
                                    <div
                                        data-testid={`pricing-trust-${t.id}`}
                                        className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full border border-digtek-purple/25 bg-digtek-purple/[0.06] text-[11px] uppercase tracking-wider font-bold text-digtek-purple"
                                    >
                                        <Lock className="w-3 h-3" />
                                        {t.trustSignal}
                                    </div>
                                )}

                                <ul className="space-y-3 mb-10">
                                    {t.features.map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-3"
                                        >
                                            <span
                                                className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                                                    t.highlighted
                                                        ? "bg-digtek-lime text-digtek-header"
                                                        : "bg-digtek-purple text-white"
                                                }`}
                                            >
                                                <Check className="w-3 h-3 stroke-[3]" />
                                            </span>
                                            <span
                                                className={`text-sm ${
                                                    t.highlighted ? "text-white/85" : "text-digtek-header"
                                                }`}
                                            >
                                                {f}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                <a
                                    href="#contact"
                                    data-testid={`pricing-cta-${t.id}`}
                                    className={
                                        t.highlighted
                                            ? "btn-digtek-lime w-full justify-center"
                                            : "btn-digtek w-full justify-center"
                                    }
                                >
                                    Get started
                                    <ArrowUpRight className="w-4 h-4" />
                                </a>
                            </div>
                        );
                    })}
                </div>

                <p className="mt-10 text-center text-sm text-digtek-text max-w-[720px] mx-auto">
                    <strong className="text-digtek-header">
                        What's not included:
                    </strong>{" "}
                    creator fees, paid media spend, influencer rates, travel,
                    studio rentals and any external production costs are billed
                    separately at cost, with prior approval.
                </p>
            </div>
        </section>
    );
}
